import { AlarmRecipientDropdown, TimeToSendDropdown } from "components";
import SC from "./style";
import { Switch } from "@mui/material";
import { TextInput } from "InnoUI/innoinput";
import theme from "theme";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { showAlert } from "utils/helper";
import { useEffect } from "react";

const AlarmContent = (props) => {
  const { setData, data } = props;
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const type = searchParams.get("type");
  const authData = useSelector((state) => state?.authdata?.data || null);

  const handleChange = (property, value, alarmType = "Primary") => {
    // Check if Primary Alarm is inactive while Secondary Alarm is being activated
    if (alarmType === "Secondary" && property === "active" && value === 1) {
      if (!data.primaryalarm.active) {
        showAlert({
          title: "Warning",
          text: "Please activate the Primary Alarm",
          icon: "warning",
        });
        return;
      }
    }

    // Handle Primary Alarm deactivation
    if (alarmType === "Primary" && property === "active" && value === 0) {
      // Check if Secondary Alarm is active while deactivating Primary Alarm
      if (data.secondaryalarm.active) {
        showAlert({
          title: "Warning",
          text: "Please deactivate the Secondary Alarm before deactivating the Primary Alarm.",
          icon: "warning",
        });
        return;
      }

      // Deactivate Primary Alarm and reset its fields
      setData((prev) => ({
        ...prev,
        primaryalarm: {
          ...prev.primaryalarm,
          active: value,
          alarmTo: authData.email,
          alarmTime: null,
          alarmDate: null,
        },
      }));
      return;
    }

    // Handle Secondary Alarm deactivation
    if (alarmType === "Secondary" && property === "active" && value === 0) {
      setData((prev) => ({
        ...prev,
        secondaryalarm: {
          ...prev.secondaryalarm,
          active: value,
          alarmTo: data.secondaryalarm.alarmTo,
          alarmTime: null,
          alarmDate: null,
          message: null,
        },
      }));
      return;
    }

    // Update other properties for Primary and Secondary Alarms
    if (alarmType === "Primary") {
      setData((prev) => ({
        ...prev,
        primaryalarm: {
          ...prev.primaryalarm,
          [property]: value,
        },
      }));
    } else if (alarmType === "Secondary") {
      setData((prev) => ({
        ...prev,
        secondaryalarm: {
          ...prev.secondaryalarm,
          [property]: value,
        },
      }));
    } else {
      setData((prev) => ({
        ...prev,
        [property]: value,
      }));
    }
  };

  return (
    <>
      {/* Primary Alarm Section */}
      <>
        <SC.HeaderContainer>
          <SC.HeaderStyle>Primary Alarm</SC.HeaderStyle>
        </SC.HeaderContainer>
        <SC.CardContainerStyle>
          {/* Alarm Recipient(s) */}
          <div style={{ display: "flex" }}>
            <SC.LabelContainer>
              <SC.Label>
                Alarm Recipient(s)
                {authData?.userType == 0 ? "" : <SC.Astrik>*</SC.Astrik>}
              </SC.Label>

              <SC.Children>
                <SC.Wrapper>
                  <AlarmRecipientDropdown
                    defaultValue={data?.primaryalarm?.alarmTo || ""}
                    name="alarmTo"
                    onChange={handleChange}
                    projectId={projectId}
                    type={type}
                    disabledDropdown={data?.primaryalarm?.active === 0}
                    disabledValues={[authData?.id]}
                  />
                </SC.Wrapper>
              </SC.Children>
            </SC.LabelContainer>
            <SC.ActivateDiv>
              <Switch
                checked={data?.primaryalarm?.active}
                onChange={(e) =>
                  handleChange("active", e.target.checked ? 1 : 0, "Primary")
                }
                inputProps={{ "aria-label": "controlled" }}
              />
              <SC.Span>Activate</SC.Span>
            </SC.ActivateDiv>
          </div>

          {/* Time To Send */}
          <SC.LabelContainer>
            <SC.Label>
              Time To Send <SC.Astrik>*</SC.Astrik>
            </SC.Label>
            <SC.Children>
              <SC.Wrapper>
                <TimeToSendDropdown
                  defaultValue={data?.primaryalarm?.alarmTime}
                  name="alarmTime"
                  onChange={handleChange}
                  disabledDropdown={data?.primaryalarm?.active === 0}
                />
              </SC.Wrapper>
            </SC.Children>
          </SC.LabelContainer>

          {/* Date To Send */}
          <SC.LabelContainer>
            <SC.Label>
              Date To Send <SC.Astrik>*</SC.Astrik>
            </SC.Label>
            <SC.Children>
              <SC.Wrapper>
                <DatePicker
                  name="alarmDate"
                  value={
                    data?.primaryalarm?.alarmDate
                      ? dayjs(data.primaryalarm.alarmDate, "YYYY-MM-DD")
                      : null
                  }
                  format="YYYY-MM-DD"
                  onChange={(value) =>
                    handleChange(
                      "alarmDate",
                      value ? dayjs(value).format("YYYY-MM-DD") : "",
                      "Primary"
                    )
                  }
                  mode="date"
                  showTime={false}
                  allowClear={false}
                  style={{ width: "100%", height: "32px", borderRadius: "3px" }}
                  disabled={data?.primaryalarm?.active === 0}
                  disabledDate={(current) =>
                    current && current.isBefore(dayjs(), "day")
                  }
                />
              </SC.Wrapper>
            </SC.Children>
          </SC.LabelContainer>
        </SC.CardContainerStyle>
      </>

      {/* Secondary Alarm Section */}
      <div style={{ marginTop: "30px" }}>
        <SC.HeaderContainer>
          <SC.HeaderStyle>Secondary Pre-Alarm </SC.HeaderStyle>
        </SC.HeaderContainer>
        <SC.CardContainerStyle>
          {/* Alarm Recipient(s) */}
          <div style={{ display: "flex" }}>
            <SC.LabelContainer>
              <SC.Label>
                Alarm Recipient(s) <SC.Astrik>*</SC.Astrik>
              </SC.Label>
              <SC.Children>
                <SC.Wrapper>
                  <AlarmRecipientDropdown
                    defaultValue={data?.secondaryalarm?.alarmTo || ""}
                    name="alarmTo"
                    onChange={handleChange}
                    projectId={projectId}
                    type={type}
                    disabledDropdown={data?.secondaryalarm?.active === 0}
                    active="Secondary"
                    // disabledValues={data?.users}
                  />
                </SC.Wrapper>
              </SC.Children>
            </SC.LabelContainer>
            <SC.ActivateDiv>
              <Switch
                checked={data?.secondaryalarm?.active}
                onChange={(e) =>
                  handleChange("active", e.target.checked ? 1 : 0, "Secondary")
                }
                inputProps={{ "aria-label": "controlled" }}
              />
              <SC.Span>Activate</SC.Span>
            </SC.ActivateDiv>
          </div>

          {/* Time To Send */}
          <SC.LabelContainer>
            <SC.Label>
              Time To Send <SC.Astrik>*</SC.Astrik>
            </SC.Label>
            <SC.Children>
              <SC.Wrapper>
                <TimeToSendDropdown
                  defaultValue={data?.secondaryalarm?.alarmTime}
                  name="alarmTime"
                  onChange={handleChange}
                  disabledDropdown={data?.secondaryalarm?.active === 0}
                  active="Secondary"
                />
              </SC.Wrapper>
            </SC.Children>
          </SC.LabelContainer>

          {/* Date To Send */}
          <SC.LabelContainer>
            <SC.Label>
              Date To Send <SC.Astrik>*</SC.Astrik>
            </SC.Label>
            <SC.Children>
              <SC.Wrapper>
                <DatePicker
                  name="alarmDate"
                  value={
                    data?.secondaryalarm?.alarmDate
                      ? dayjs(data.secondaryalarm.alarmDate, "YYYY-MM-DD")
                      : null
                  }
                  format="YYYY-MM-DD"
                  onChange={(value) =>
                    handleChange(
                      "alarmDate",
                      value ? dayjs(value).format("YYYY-MM-DD") : "",
                      "Secondary"
                    )
                  }
                  mode="date"
                  showTime={false}
                  allowClear={false}
                  style={{ width: "100%", height: "32px", borderRadius: "3px" }}
                  disabled={data?.secondaryalarm?.active === 0}
                  disabledDate={(current) =>
                    current && current.isBefore(dayjs(), "day")
                  }
                />
              </SC.Wrapper>
            </SC.Children>
          </SC.LabelContainer>

          {/* Message */}
          <SC.LabelContainer>
            <SC.Label>
              Message <SC.Astrik>*</SC.Astrik>
            </SC.Label>
            <SC.Children>
              <SC.Wrapper>
                <TextInput
                  name="message"
                  defaultValue={data?.secondaryalarm?.message}
                  value={data?.secondaryalarm?.message}
                  onChange={(value) =>
                    handleChange("message", value, "Secondary")
                  }
                  isTextArea
                  size="large"
                  style={{
                    width: "100%",
                    height: "50px",
                    fontSize: "12px",
                    border: `1px solid ${theme.colors.bordertask}`,
                    borderRadius: "3px",
                    resize: "none",
                  }}
                  disabled={data?.secondaryalarm?.active === 0}
                />
              </SC.Wrapper>
            </SC.Children>
          </SC.LabelContainer>
        </SC.CardContainerStyle>
      </div>
    </>
  );
};

export default AlarmContent;
